import type { ComponentProps } from 'react';

import { Toaster as Sonner, toast as sonnerToast } from 'sonner';

import type { ToastHistoryType } from './ToastHistory/toastHistoryStore';

import { useTheme } from '../providers/ThemeProvider';
import { markToastHistoryDismissed, recordToastHistory } from './ToastHistory/toastHistoryStore';

type ToasterProps = ComponentProps<typeof Sonner>;

type SonnerToast = typeof sonnerToast;

type ToastMessage = Parameters<SonnerToast>[0];

type ToastOptions = Parameters<SonnerToast>[1];

type ToastId = ReturnType<SonnerToast>;

type TypedToastFn = typeof sonnerToast.success;

type TypedToastMessage = Parameters<TypedToastFn>[0];

interface ToastHistoryConfig {
  title: string;
  description?: string;
}

interface ToastOptionsWithHistory extends NonNullable<ToastOptions> {
  history?: ToastHistoryConfig | false;
}

type ToastFnWithHistory = (message: TypedToastMessage, options?: ToastOptionsWithHistory) => ToastId;

interface ToastWithHistory {
  (message: ToastMessage, options?: ToastOptionsWithHistory): ToastId;
  success: ToastFnWithHistory;
  info: ToastFnWithHistory;
  warning: ToastFnWithHistory;
  error: ToastFnWithHistory;
  loading: ToastFnWithHistory;
  message: ToastFnWithHistory;
  custom: SonnerToast['custom'];
  promise: SonnerToast['promise'];
  dismiss: SonnerToast['dismiss'];
  getHistory: SonnerToast['getHistory'];
  getToasts: SonnerToast['getToasts'];
}

const getToastHistory = (options?: ToastOptionsWithHistory) => {
  return options?.history;
};

const getSonnerOptions = (options?: ToastOptionsWithHistory): ToastOptions => {
  if (!options) return undefined;

  const { history, ...sonnerOptions } = options;

  if (!history) return sonnerOptions;

  return {
    ...sonnerOptions,
    onDismiss: (toastItem) => {
      markToastHistoryDismissed(toastItem.id);
      sonnerOptions.onDismiss?.(toastItem);
    },
    onAutoClose: (toastItem) => {
      markToastHistoryDismissed(toastItem.id);
      sonnerOptions.onAutoClose?.(toastItem);
    },
  };
};

const recordToast = (type: ToastHistoryType, toastId: ToastId, history?: ToastHistoryConfig | false) => {
  if (!history) return;

  recordToastHistory({
    toastId,
    type,
    title: history.title,
    description: history.description,
  });
};

const createTypedToast = (type: ToastHistoryType, toastFn: TypedToastFn): ToastFnWithHistory => {
  return (message, options) => {
    const toastId = toastFn(message, getSonnerOptions(options));
    recordToast(type, toastId, getToastHistory(options));

    return toastId;
  };
};

const defaultToast = (message: ToastMessage, options?: ToastOptionsWithHistory): ToastId => {
  const toastId = sonnerToast(message, getSonnerOptions(options));
  recordToast('default', toastId, getToastHistory(options));

  return toastId;
};

const toast: ToastWithHistory = Object.assign(defaultToast, sonnerToast, {
  success: createTypedToast('success', sonnerToast.success),
  error: createTypedToast('error', sonnerToast.error),
  info: createTypedToast('info', sonnerToast.info),
  warning: createTypedToast('warning', sonnerToast.warning),
  loading: createTypedToast('loading', sonnerToast.loading),
  message: createTypedToast('message', sonnerToast.message),
  dismiss: (id?: string | number) => {
    const result = sonnerToast.dismiss(id);
    markToastHistoryDismissed(id);

    return result;
  },
});

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:p-4',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          closeButton: '',
        },
      }}
      {...props}
    />
  );
};

export { toast, Toaster };
